import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { X, Download, Printer, Loader2, Check, Minus } from "lucide-react";

/** Full access cross-tab viewer with CSV export + print. props: onClose */
export default function AccessMatrix({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({ roles: [], rows: [] });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await api.get("/v1/menu-registry/access-matrix");
      setData(res.data.data);
    } catch (e) {
      setError(apiErrorMessage(e.response?.data?.detail) || "Failed to load access matrix");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const headers = ["Sl", "Module", "Menu", "Permission Code", ...data.roles.map((r) => r.roleCode)];
    const lines = [headers.join(",")];
    data.rows.forEach((row) => {
      const cells = [
        row.sl_no, `"${row.module}"`, `"${row.menu}"`, row.permission_code,
        ...data.roles.map((r) => (row.granted[r.roleCode] ? "Y" : "")),
      ];
      lines.push(cells.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reasonconnectiq-access-matrix-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <div className="fixed inset-0 z-[120] flex flex-col bg-background" data-testid="access-matrix-modal">
      <style>{`@media print { .am-noprint { display:none !important; } .am-scroll { overflow: visible !important; height:auto !important; } body * { visibility: hidden; } #am-print, #am-print * { visibility: visible; } #am-print { position:absolute; left:0; top:0; width:100%; } }`}</style>

      <div className="am-noprint flex items-center justify-between border-b border-border bg-card px-6 py-4">
        <div>
          <h2 className="text-lg font-bold text-foreground">Access Matrix</h2>
          <p className="text-xs text-muted-foreground">{data.rows.length} menus × {data.roles.length} roles · Y = access granted</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50" data-testid="am-export-csv">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button onClick={() => window.print()} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50" data-testid="am-print">
            <Printer className="h-4 w-4" /> Print
          </button>
          <button onClick={onClose} className="rounded-md p-2 hover:bg-muted" data-testid="am-close"><X className="h-5 w-5" /></button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : error ? (
        <div className="m-6 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
      ) : (
        <div className="am-scroll flex-1 overflow-auto" id="am-print">
          <table className="w-full border-collapse text-xs">
            <thead className="sticky top-0 z-10">
              <tr className="bg-card">
                <th className="sticky left-0 z-20 border border-border bg-card px-2 py-2 text-left font-semibold">#</th>
                <th className="sticky left-8 z-20 border border-border bg-card px-2 py-2 text-left font-semibold">Module</th>
                <th className="border border-border bg-card px-2 py-2 text-left font-semibold">Menu</th>
                {data.roles.map((r) => (
                  <th key={r.roleCode} className="border border-border bg-card px-1.5 py-2 align-bottom font-semibold" title={r.roleName}>
                    <span className="block whitespace-nowrap [writing-mode:vertical-rl] rotate-180 text-[10px]">{r.roleCode}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.permission_code} className="hover:bg-muted/40" data-testid={`am-row-${row.permission_code}`}>
                  <td className="sticky left-0 border border-border bg-background px-2 py-1 text-muted-foreground">{row.sl_no}</td>
                  <td className="sticky left-8 border border-border bg-background px-2 py-1 text-muted-foreground">{row.module}</td>
                  <td className="border border-border bg-background px-2 py-1 font-medium text-foreground whitespace-nowrap">{row.menu}</td>
                  {data.roles.map((r) => (
                    <td key={r.roleCode} className={`border border-border px-1.5 py-1 text-center ${row.granted[r.roleCode] ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-background"}`}>
                      {row.granted[r.roleCode]
                        ? <Check className="mx-auto h-3.5 w-3.5 text-emerald-600" />
                        : <Minus className="mx-auto h-3 w-3 text-muted-foreground/30" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>,
    document.body
  );
}
